import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

interface PageItem {
  type: 'page' | 'ellipsis';
  value: number;
}

const MAX_VISIBLE_PAGES = 7;
const EDGE_PAGES = 1;
const SIBLINGS = 1;

@Component({
  selector: 'x-pagination',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './x-pagination.component.html',
  styleUrl: './x-pagination.component.scss',
})
export class XPaginationComponent {
  currentPage = input.required<number>();
  totalItems = input.required<number>();
  pageSize = input(20);

  pageChange = output<number>();

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalItems() / this.pageSize())),
  );

  readonly canGoPrevious = computed(() => this.currentPage() > 1);
  readonly canGoNext = computed(() => this.currentPage() < this.totalPages());

  readonly pages = computed(() => buildPageItems(this.currentPage(), this.totalPages()));

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.pageChange.emit(page);
  }

  previousPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }
}

function buildPageItems(current: number, total: number): readonly PageItem[] {
  if (total <= MAX_VISIBLE_PAGES) {
    return createRange(1, total).map(toPageItem);
  }

  const leftSibling = Math.max(current - SIBLINGS, EDGE_PAGES + 2);
  const rightSibling = Math.min(current + SIBLINGS, total - EDGE_PAGES - 1);

  const items: PageItem[] = [];

  items.push(toPageItem(1));

  if (leftSibling > EDGE_PAGES + 2) {
    items.push({ type: 'ellipsis', value: -1 });
  } else {
    items.push(toPageItem(2));
  }

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (i > 1 && i < total) {
      items.push(toPageItem(i));
    }
  }

  if (rightSibling < total - EDGE_PAGES - 1) {
    items.push({ type: 'ellipsis', value: -2 });
  } else {
    items.push(toPageItem(total - 1));
  }

  items.push(toPageItem(total));

  return deduplicatePages(items);
}

function toPageItem(page: number): PageItem {
  return { type: 'page', value: page };
}

function createRange(start: number, end: number): readonly number[] {
  const result: number[] = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
}

function deduplicatePages(items: readonly PageItem[]): readonly PageItem[] {
  const seen = new Set<number>();
  const result: PageItem[] = [];

  for (const item of items) {
    if (item.type === 'ellipsis') {
      result.push(item);
      continue;
    }
    if (seen.has(item.value)) {
      continue;
    }
    seen.add(item.value);
    result.push(item);
  }

  return result;
}
